import { ApolloError } from '@apollo/client';
import {
  authenticationChannel,
  authenticationContext,
  sessionManagement,
  ActivityMonitor,
  commonActions
} from '@codametrix/ui-common';
import { shouldAttachBearerAuthorization } from '@codametrix/client-api';
import { showModalMessage } from '../actions/feedback.actions';

const { activityMonitor } = sessionManagement;

const isAuthError = (e: unknown): boolean => {
  if (e instanceof ApolloError) {
    if ((e.networkError as any)?.statusCode === 401) return true;
    if (e.graphQLErrors.some(err => err.extensions?.code === 'UNAUTHENTICATED'))
      return true;
  }
  if (e instanceof Error && e.message.includes('No authentication token'))
    return true;
  return false;
};

const dispatchLogoutModal = () => {
  // Lazy require to avoid circular dependency:
  // service-desk.client → store → reducers → actions → service-desk.client
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const store = require('../store/store').default;
  store.dispatch(
    showModalMessage({
      title: 'You have been logged out.',
      buttonText: 'Log in',
      closable: false,
      handler: commonActions.UserInterface.LOGOUT,
      forceSubmitOptions: {
        isForced: true,
        message: 'Navigating to login page in ',
        time: 10
      }
    }) as any
  );
};

/** Mirrors service-desk `ServiceDeskApi` — UMD host cannot import the package. */
type ServiceDeskApi = {
  apollo: any;
  getBearerToken: () => Promise<string>;
  dispose: () => void;
};

type ServiceDeskWidget = {
  api: ServiceDeskApi;
  apolloClient: any;
  refreshToken: (...args: any[]) => Promise<string>;
  create: (...args: any[]) => void;
};

let _client: ServiceDeskWidget | null = null;
let _worklistApi: ServiceDeskApi | null = null;
let _didRefreshListener: (() => void) | null = null;
let _logoutResetRegistered = false;

const getBearerToken = async (): Promise<string> => {
  await authenticationChannel.ready;
  // Under FRONTEND_SESSION_FIRST, API calls rely on the session cookie and
  // never mint a client JWT — an empty token here is expected, not a sign
  // the user is logged out (see shouldAttachBearerAuthorization). Mirrors the
  // same gate create-service-desk-api.ts's own authLink/fetch already honor;
  // this covers the ServiceDesk.init(...) call site, which doesn't go through
  // that wrapper.
  if (!shouldAttachBearerAuthorization()) {
    return '';
  }
  const token = authenticationContext.getToken();
  if (!token) {
    throw new Error('No authentication token available for Service Desk');
  }
  if (token.indexOf('Bearer') !== -1) {
    return token;
  }
  return `Bearer ${token}`;
};

const wrapApolloClient = (apolloClient: any) => {
  const origQuery = apolloClient.query.bind(apolloClient);
  apolloClient.query = async (...args: any[]) => {
    try {
      return await origQuery(...args);
    } catch (e) {
      if (isAuthError(e)) dispatchLogoutModal();
      throw e;
    }
  };

  const origMutate = apolloClient.mutate.bind(apolloClient);
  apolloClient.mutate = async (...args: any[]) => {
    try {
      return await origMutate(...args);
    } catch (e) {
      if (isAuthError(e)) dispatchLogoutModal();
      throw e;
    }
  };
};

/** Exposed only for unit tests — resets the singleton so each test gets a fresh client. */
export const _resetClientForTesting = () => {
  _client = null;
  _worklistApi = null;
  _didRefreshListener = null;
};

const registerLogoutReset = () => {
  if (_logoutResetRegistered) return;
  globalThis.addEventListener('cmx-logout', () => {
    if (_didRefreshListener) {
      activityMonitor.off(ActivityMonitor.events.DID_REFRESH, _didRefreshListener);
      _didRefreshListener = null;
    }
    _worklistApi?.dispose();
    _worklistApi = null;
    //@ts-ignore — UMD global
    ServiceDesk.setServiceDeskApi?.(null);
    _client = null;
  });
  _logoutResetRegistered = true;
};

/**
 * Worklist Service Desk API (CDMX-20574). Live bearer; separate from QA Apollo cache.
 */
export const getWorklistServiceDeskApi = (): ServiceDeskApi => {
  registerLogoutReset();
  if (_worklistApi) return _worklistApi;

  //@ts-ignore — UMD global from service-desk artifact
  _worklistApi = ServiceDesk.createServiceDeskApi({
    bffURL: '/api/service-desk/bff',
    getBearerToken,
    cacheProfile: 'worklist',
    onApolloClientReady: wrapApolloClient
  });
  //@ts-ignore
  ServiceDesk.setServiceDeskApi(_worklistApi);
  return _worklistApi!;
};

export const getServiceDeskClient = () => {
  registerLogoutReset();
  if (_client) return _client;

  const api = getWorklistServiceDeskApi();

  //@ts-ignore — UMD global
  const xcaliberClient = ServiceDesk.init({
    theme: {},
    bffURL: '/api/service-desk/bff',
    getBearerToken,
    api
    // wrapApolloClient already applied via createServiceDeskApi onApolloClientReady
  });

  // Optional: reset Apollo cache on renew so lists reflect new session; auth is live.
  _didRefreshListener = async () => {
    try {
      await api.apollo.clearStore?.();
    } catch {
      // ignore cache reset failures
    }
    try {
      await xcaliberClient.refreshToken();
    } catch (e) {
      if (isAuthError(e)) dispatchLogoutModal();
    }
  };
  activityMonitor.on(ActivityMonitor.events.DID_REFRESH, _didRefreshListener);

  _client = xcaliberClient;

  const origRefreshToken = xcaliberClient.refreshToken.bind(xcaliberClient);
  xcaliberClient.refreshToken = async (...args: any[]) => {
    try {
      return await origRefreshToken(...args);
    } catch (e) {
      if (isAuthError(e)) dispatchLogoutModal();
      throw e;
    }
  };

  return _client;
};
